<script setup>
// @ts-check
import { ref, toRef } from 'vue'
import { useErrorMessage } from '@/studentsite/common/useErrorMessage'
import UniversitySelection from '../PartsUniversitySelection/UniversitySelection.vue'
import DepartmentSelection from '../PartsDepartmentSelection/DepartmentSelection.vue'
import { setup } from '@/studentsite/signup/signup/components/University/Form/useUniversityForm'
import { getMaster } from '../../../Master.vue'

/**
 * @import { Ref, PropType } from 'vue'
 * @import { Props, Emits } from './emitTypes'
 */

/**
 * @type Emits
 */
const emits = defineEmits(['setParam', 'submit'])

const props = defineProps({
  inputErrors: {
    /** @type {PropType<Props["inputErrors"]>} */
    type: Object,
    required: true,
  },
})
const { has: errorHas, get: getError } = useErrorMessage(toRef(() => props.inputErrors).value)
const humanitiesOrScienceMaster = getMaster('humanities_or_science_master') ?? {}
const schoolDepartmentMaster = getMaster('school_department_master') ?? {}
/** @type {Ref<UniversitySelection|undefined>} */
const refUniversitySelection = ref()
/** @type {Ref<DepartmentSelection|undefined>} */
const refDepartmentSelection = ref()

const {
  universityName,
  universityCd,
  isAbroad,
  faculty,
  facultyCd,
  humanitiesOrScience,
  schoolDepartment,
  graduationYear,
  graduationMonth,
  acceptTerm,
  submittable,

  universityList,
  departmentList,
  graduationYearList,
  graduationMonthList,

  onChangeUniversityName,
  onChangeUniversitySelection,
  onChangeUniversityDepartmentName,
  onChangeUniversityDepartmentSelection,
  onChangeAbroad,
  submit,

  listUniversityNameValidation,
  listUniversityDepartmentNameValidation,
  checkUniversityName,
  checkUniversityDepartmentName,
  isValidGraduationConsistency,
  listGraduationConsistencyValidation,
  checkGraduateYearMonth,
} = setup(refDepartmentSelection, refUniversitySelection, emits)

const useUrl = /** @type {HTMLInputElement|undefined} */ (document.getElementById('url_use'))?.value
const privacyPolicyUrl = /** @type {HTMLInputElement|undefined} */ (document.getElementById('url_privacy_policy'))
  ?.value
</script>

<template>
  <div class="container container--signup">
    <div class="navWrap">
      <div class="navArea">
        <ul class="stepBar stepBar--3step">
          <li class="step active">基本情報</li>
          <li class="step active">大学情報</li>
          <li class="step">登録完了</li>
        </ul>
      </div>
    </div>
    <div class="contentWrap">
      <div class="formArea">
        <div class="formSet">
          <div class="title">
            学校名
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form">
            <UniversitySelection
              ref="refUniversitySelection"
              :default="universityName"
              :university-list="universityList"
              :input-errors="inputErrors"
              :validation-errors="listUniversityNameValidation"
              @input-change="onChangeUniversityName"
              @selection-change="onChangeUniversitySelection"
              @text-change="checkUniversityName"
            />
            <input
              type="hidden"
              name="university_cd"
              v-model="universityCd"
            />
            <p
              class="errorMessage"
              v-show="errorHas('university_cd')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >学校名はサジェストから選択してください
            </p>
            <p
              class="errorMessage"
              v-if="listUniversityNameValidation.length"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ listUniversityNameValidation[0] }}
            </p>
            <p
              class="errorMessage"
              v-else-if="errorHas('university_name')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ getError('university_name') }}
            </p>
            <div class="md_toggle abroadCheck">
              <input
                type="checkbox"
                name="abroad"
                id="abroad"
                :checked="isAbroad"
                @change="onChangeAbroad"
                value="1"
              />
              <label for="abroad">海外大学</label>
            </div>
          </div>
        </div>
        <div class="formSet">
          <div class="title">
            学部/研究科
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form">
            <DepartmentSelection
              ref="refDepartmentSelection"
              :default="faculty"
              :department-list="departmentList"
              :input-errors="inputErrors"
              :validation-errors="listUniversityDepartmentNameValidation"
              :is-abroad="isAbroad"
              @input-change="onChangeUniversityDepartmentName"
              @selection-change="onChangeUniversityDepartmentSelection"
              @text-change="checkUniversityDepartmentName"
            />
            <input
              type="hidden"
              name="faculty_cd"
              v-model="facultyCd"
            />
            <p
              class="errorMessage"
              v-show="errorHas('faculty_cd')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >学部/研究科はサジェストから選択してください
            </p>
            <p
              class="errorMessage"
              v-if="listUniversityDepartmentNameValidation.length"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ listUniversityDepartmentNameValidation[0] }}
            </p>
            <p
              class="errorMessage"
              v-else-if="errorHas('faculty')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ getError('faculty') }}
            </p>
          </div>

          <div class="form">
            <span class="md_select form--w100">
              <select
                v-model="humanitiesOrScience"
                name="humanities_or_science"
                class="form--line"
                :class="{ md_error: errorHas('humanities_or_science') }"
              >
                <option :value="0">文理区分を選択してください</option>
                <option
                  v-for="(label, val) in humanitiesOrScienceMaster"
                  :key="val"
                  :value="val"
                  v-text="label"
                />
              </select>
            </span>
            <p
              class="errorMessage"
              v-show="errorHas('humanities_or_science')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ getError('humanities_or_science') }}
            </p>
          </div>
          <div class="form">
            <span class="md_select form--w100">
              <select
                v-model="schoolDepartment"
                name="school_department"
                class="form--line"
                :class="{ md_error: errorHas('school_department') }"
              >
                <option :value="0">学科系統を選択してください</option>
                <option
                  v-for="_rec in schoolDepartmentMaster"
                  :key="`department_detail_list_${_rec.id}`"
                  :value="_rec.id"
                  v-text="_rec.value"
                />
              </select>
            </span>
            <p
              class="errorMessage"
              v-show="errorHas('school_department')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ getError('school_department') }}
            </p>
          </div>
        </div>
        <div class="formSet formSet--graduation">
          <div class="title">
            卒業年
            <div class="md_label md_label--must">必須</div>
          </div>
          <div class="form column">
            <div class="columnWrap">
              <span class="md_select form--w100">
                <select
                  name="graduation_year"
                  v-model="graduationYear"
                  class="form--line"
                  :class="{
                    md_error:
                      errorHas('graduation_year') ||
                      errorHas('graduation_consistency') ||
                      !isValidGraduationConsistency,
                  }"
                  @change="checkGraduateYearMonth"
                >
                  <option :value="0">卒年</option>
                  <option
                    v-for="num in graduationYearList"
                    :key="`graduation_year_list_${num}`"
                    :value="num"
                    v-text="num"
                  />
                </select>
              </span>
              <p
                class="errorMessage"
                v-show="errorHas('graduation_year')"
              >
                <span class="iconImgPart">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 150 1100 1100"
                  >
                    <path
                      d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                      fill="#f04438"
                    />
                  </svg> </span
                >{{ getError('graduation_year') }}
              </p>
            </div>
            <span class="md_select form--w30">
              <select
                name="graduation_month"
                v-model="graduationMonth"
                class="form--line"
                :class="{
                  md_error:
                    errorHas('graduation_month') || errorHas('graduation_consistency') || !isValidGraduationConsistency,
                }"
                @change="checkGraduateYearMonth"
              >
                <option
                  v-for="num in graduationMonthList"
                  :key="`month_list_${num}`"
                  :value="num"
                  v-text="num + '月'"
                />
              </select>
            </span>
            <p
              class="errorMessage"
              v-show="errorHas('graduation_month')"
            >
              <span class="iconImgPart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 150 1100 1100"
                >
                  <path
                    d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                    fill="#f04438"
                  />
                </svg> </span
              >{{ getError('graduation_month') }}
            </p>
          </div>
          <p
            class="errorMessage"
            v-if="errorHas('graduation_consistency')"
          >
            <span class="iconImgPart">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 150 1100 1100"
              >
                <path
                  d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                  fill="#f04438"
                />
              </svg> </span
            >{{ getError('graduation_consistency') }}
          </p>
          <p
            class="errorMessage"
            v-else-if="listGraduationConsistencyValidation.length"
          >
            <span class="iconImgPart">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 150 1100 1100"
              >
                <path
                  d="m993.554 865.69-.267-.267-364.968-609.718c0-.267-.267-.267-.267-.544-23.986-38.818-65.23-61.994-110.515-61.994s-86.524 23.182-110.246 61.727c-.267.267-.544.544-.544 1.078L41.509 865.69s0 .267-.267.267c-11.321 19.409-17.52 42.051-17.52 64.96 0 70.622 57.416 128.574 127.767 129.382h731.833c70.622-.811 128.036-58.76 128.036-129.382 0-22.909-6.199-45.551-17.787-65.23zM882.5 974.05H152.564c-23.453-.267-42.318-19.676-42.318-43.129 0-7.544 1.889-14.821 5.66-21.298l364.968-609.448.267-.267c8.088-12.665 21.564-20.22 36.661-20.22s28.841 7.821 36.929 20.753L919.159 909.35c3.777 6.466 5.66 14.021 5.66 21.564.267 23.453-18.865 42.86-42.318 43.129zM517.53 456.516c-23.72 0-43.129 19.409-43.129 43.129v172.51c0 23.72 19.409 43.129 43.129 43.129s43.129-19.409 43.129-43.129v-172.51c0-23.72-19.409-43.129-43.129-43.129zm0 345.023c-11.321 0-22.375 4.577-30.461 12.665s-12.665 19.142-12.665 30.461 4.577 22.375 12.665 30.461c8.088 8.088 19.142 12.665 30.461 12.665s22.375-4.577 30.461-12.665c8.088-8.088 12.665-19.142 12.665-30.461s-4.577-22.375-12.665-30.461c-8.088-8.088-19.142-12.665-30.461-12.665z"
                  fill="#f04438"
                />
              </svg> </span
            >{{ listGraduationConsistencyValidation[0] }}
          </p>
        </div>
      </div>
      <div class="btnArea">
        <div class="toggleSet">
          <div class="md_toggle">
            <input
              id="policy"
              v-model="acceptTerm"
              type="checkbox"
              name=""
              value=""
            />
            <label for="policy"
              ><a
                :href="useUrl"
                class="md_textLink"
                target="_blank"
                >iroots利用規約</a
              >と<a
                :href="privacyPolicyUrl"
                class="md_textLink"
                target="_blank"
                >プライバシーポリシー</a
              >に同意する</label
            >
          </div>
        </div>
        <div class="btnSet">
          <a
            href="javascript:void(0)"
            class="md_btn md_btn--submit"
            :class="{ 'md_btn--disable': !submittable }"
            @click="submit"
            >同意して登録</a
          >
        </div>
      </div>
    </div>
  </div>
</template>
